/**
 * Quanto tempo uma autorização VIDaaS vale.
 * Padrão: a cada documento. «12 horas» reutiliza `signature_session` neste aparelho.
 * Espelho de `supabase/functions/_shared/vidaasSessionMode.ts`.
 */
export type VidaasSessionMode = 'each_document' | 'twelve_hours'

export const DEFAULT_VIDAAS_SESSION_MODE: VidaasSessionMode = 'each_document'

export const VIDAAS_SESSION_MODE_LABEL: Record<VidaasSessionMode, string> = {
  each_document: 'A cada documento',
  twelve_hours: 'Por 12 horas',
}

export const VIDAAS_EACH_DOCUMENT_LIFETIME_SEC = 900
export const VIDAAS_TWELVE_HOURS_LIFETIME_SEC = 43_200

export function isVidaasSessionMode(v: unknown): v is VidaasSessionMode {
  return v === 'each_document' || v === 'twelve_hours'
}

export function parseVidaasSessionMode(raw: unknown): VidaasSessionMode {
  return isVidaasSessionMode(raw) ? raw : DEFAULT_VIDAAS_SESSION_MODE
}

export function vidaasAuthorizeForMode(mode: VidaasSessionMode): {
  lifetimeSec: number
  scope: 'signature_session'
} {
  if (mode === 'twelve_hours') {
    return { lifetimeSec: VIDAAS_TWELVE_HOURS_LIFETIME_SEC, scope: 'signature_session' }
  }
  // Mesmo escopo já aceito pelo PSC. A autorização única vem de não guardar o token.
  return { lifetimeSec: VIDAAS_EACH_DOCUMENT_LIFETIME_SEC, scope: 'signature_session' }
}

/** Preferência de autorização no app do certificado (VIDaaS). */
export type VidaasAuthPreference = 'qrcode' | 'push' | 'ask_each_time'

export const VIDAAS_AUTH_PREFERENCE_LABEL: Record<VidaasAuthPreference, string> = {
  ask_each_time: 'Perguntar a cada vez',
  qrcode: 'Navegador (QR Code)',
  push: 'Notificação no app',
}

/** Padrão de produto: notificação no app VIDaaS. */
export const DEFAULT_VIDAAS_AUTH_PREFERENCE: VidaasAuthPreference = 'push'

export function isVidaasAuthPreference(v: unknown): v is VidaasAuthPreference {
  return v === 'qrcode' || v === 'push' || v === 'ask_each_time'
}

export function parseVidaasAuthPreference(raw: unknown): VidaasAuthPreference {
  return isVidaasAuthPreference(raw) ? raw : DEFAULT_VIDAAS_AUTH_PREFERENCE
}

/** Método inicial no diálogo quando a preferência é «perguntar a cada vez». */
export function resolveInitialVidaasAuthMethod(
  preference: VidaasAuthPreference,
): 'qrcode' | 'push' {
  return preference === 'qrcode' ? 'qrcode' : 'push'
}
